"use client";

import { usePathname, useRouter } from "next/navigation";
import { useCallback } from "react";

import { useFiltersStore } from "../stores/filters.store";
import {
  catalogFilterClick,
  extractAppliedFilterFields,
} from "../tracking/catalogPage.tracking";
import { parseCatalogRoute } from "../types/catalogRoute.helper";
import type { PendingFilters } from "../types/filters.type";
import { filterParsers, useFilterParams } from "./useFilterParams.hook";
import type { FilterParamKey } from "./useFilterParams.hook";
import { useFiltersQuery } from "./useFiltersQuery.hook";

function isPathParam(field: string, pathname: string): boolean {
  if (field === "brand") return pathname.startsWith("/b/");
  return false;
}

type SetFilterParams = ReturnType<typeof useFilterParams>[1];
type FilterParamUpdate = Parameters<SetFilterParams>[0];

export function useFilterActions() {
  const router = useRouter();
  const pathname = usePathname();
  const [filterParams, setFilterParams] = useFilterParams();
  const { facets: appliedFacets } = useFiltersQuery();

  const removeDraftFilter = useCallback((field: string, value: string) => {
    useFiltersStore.getState().removePendingFilter(field, value);
  }, []);

  const clearDraftFilters = useCallback(() => {
    useFiltersStore.setState((state) => {
      if (!state.pendingFilters) return state;

      const cleared: PendingFilters = {};

      if (state.pendingFilters.brand && isPathParam("brand", pathname)) {
        cleared.brand = state.pendingFilters.brand;
      }

      if (
        state.pendingFilters.pending_category_path &&
        parseCatalogRoute(pathname).isCategoryDrill
      ) {
        cleared.pending_category_path =
          state.pendingFilters.pending_category_path;
      }

      return { pendingFilters: cleared };
    });
  }, [pathname]);

  const applyFilters = useCallback(() => {
    const { pendingFilters, discardDraft } = useFiltersStore.getState();

    if (!pendingFilters) return;

    catalogFilterClick(extractAppliedFilterFields(pendingFilters));

    const { pending_category_path, ...rest } = pendingFilters;

    const route = parseCatalogRoute(pathname);
    const currentSlugPath = route.isCategoryDrill ? route.categoryPath : null;
    const categoryPathChanged =
      !!pending_category_path && pending_category_path !== currentSlugPath;

    if (categoryPathChanged) {
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(rest)) {
        if (value === null || value === undefined) continue;
        if (Array.isArray(value) && value.length > 0)
          params.set(key, value.join(","));
        else if (typeof value === "boolean" && value) params.set(key, "true");
        else if (typeof value === "number" && !isNaN(value))
          params.set(key, String(value));
      }

      const qs = params.toString();
      router.push(
        `${route.basePrefix}${pending_category_path}${qs ? `?${qs}` : ""}`,
      );
    } else {
      const nuqsParams = Object.fromEntries(
        (Object.keys(filterParsers) as FilterParamKey[])
          .filter((key) => !isPathParam(key, pathname))
          .map((key) => {
            const value = (rest as Record<string, unknown>)[key];
            if (Array.isArray(value) && value.length === 0) return [key, null];
            return [key, value ?? null];
          }),
      ) as FilterParamUpdate;

      setFilterParams(nuqsParams);
    }

    discardDraft();
  }, [pathname, router, setFilterParams]);

  const removeAppliedFilter = useCallback(
    (field: string, value: string) => {
      const key = field as FilterParamKey;
      const current = filterParams[key];
      if (Array.isArray(current)) {
        const updated = (current as string[]).filter((v) => v !== value);
        setFilterParams({
          [key]: updated.length > 0 ? updated : null,
        } as FilterParamUpdate);
      } else {
        setFilterParams({ [key]: null } as FilterParamUpdate);
      }
    },
    [filterParams, setFilterParams],
  );

  const clearAllAppliedFilters = useCallback(() => {
    const resetParams = Object.fromEntries(
      (Object.keys(filterParsers) as FilterParamKey[])
        .filter((key) => !isPathParam(key, pathname))
        .map((key) => [key, null]),
    ) as FilterParamUpdate;

    setFilterParams(resetParams);
  }, [pathname, setFilterParams]);

  const getAppliedValues = useCallback(
    (field: string) => {
      const facet = appliedFacets.find((f) => f.field === field);
      if (
        !facet ||
        (facet.component === "Default" && facet.selector === "Boolean") ||
        facet.component === "CustomPrice" ||
        facet.component === "CustomCategory"
      ) {
        return [];
      }
      if (facet.component === "Default" || facet.component === "CustomColor") {
        return (facet as { values: { is_selected: boolean }[] }).values.filter(
          (v) => v.is_selected,
        );
      }
      return [];
    },
    [appliedFacets],
  );

  const getActiveFilterFields = useCallback(
    () => appliedFacets.filter((f) => f.is_selected).map((f) => f.field),
    [appliedFacets],
  );

  return {
    removeDraftFilter,
    clearDraftFilters,
    applyFilters,
    removeAppliedFilter,
    clearAllAppliedFilters,
    getAppliedValues,
    getActiveFilterFields,
  };
}
